/**
 * PreviewCardCat — "How it looks in the library" preview for a
 * Category. Mirrors TheTappingSolutionWeb's `CategoryCardVariable`
 * (src/components/cards/CategoryCardVariable.tsx):
 *
 *   • Full-bleed background image (object-cover)
 *   • Half-height bottom gray gradient overlay
 *     (linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.65) 100%))
 *   • Title at the top-left (Asap, uppercase semibold, white)
 *   • Subitem count at the bottom-left ("N sessions" / "N videos")
 *   • CTA play icon at the bottom-right — uses
 *     WebAppSessionPlayIcon for regular categories or
 *     WebAppQuickTapVideoIcon when category_is_quick_tap, so the
 *     glyph matches whatever the user will see in the WebApp.
 *
 * Footer: universal_link path + Open ↗ link, same as the session
 * preview card. Falls back to a "not yet generated" hint on
 * brand-new categories.
 */
import React from 'react';
import RailCard, { RailCardHead } from '../../redesign/edit/RailCard';
import {
  WebAppSessionPlayIcon,
  WebAppQuickTapVideoIcon,
} from '../../redesign/edit/webappPreviewIcons';

export default function PreviewCardCat({ state, subitemCount }) {
  const isQuickTap = !!state.category_is_quick_tap;
  const imageUrl = state.category_image_preview || state.category_image || '';
  const title = (state.category_title || 'Untitled category').toUpperCase();
  const count = Number(subitemCount) || 0;
  const countLabel = `${count} ${isQuickTap ? (count === 1 ? 'video' : 'videos') : (count === 1 ? 'session' : 'sessions')}`;

  const universalLink = state.universal_link || null;
  const previewPath = universalLink
    ? universalLink.replace(/^https?:\/\//, '')
    : '— not yet generated —';

  return (
    <RailCard
      variant="preview"
      head={({ open, toggle }) => (
        <RailCardHead
          variant="preview"
          eyebrow="How it looks in the library"
          open={open}
          onToggle={toggle}
        />
      )}
    >
      {/* Stage */}
      <div
        style={{
          padding: 16,
          background: 'linear-gradient(180deg, #FAF7F2 0%, #F5EDE4 100%)',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 240,
            aspectRatio: '216 / 268',
            borderRadius: 14,
            overflow: 'hidden',
            position: 'relative',
            backgroundImage: imageUrl ? `url("${imageUrl}")` : 'none',
            backgroundColor: imageUrl ? 'transparent' : '#2C3E50',
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            boxShadow: '0 8px 20px #2C3E5025, 0 1px 0 #FFFFFF80 inset',
          }}
        >
          {/* `.bg-gradient-tile-gray-overlay` — half-height bottom
              fade. Categories use this as the ONLY overlay
              (subcategories layer an extra full-height black
              gradient — see PreviewCardSub). */}
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: '50%',
              background:
                'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.65) 100%)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />

          {/* Title — top-left */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              padding: '12px 14px',
              zIndex: 2,
            }}
          >
            <h3
              style={{
                margin: 0,
                fontFamily: 'Asap, sans-serif',
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.25,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                color: '#FFFFFF',
                textShadow: '0 1px 4px #00000060',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                maxWidth: '90%',
              }}
            >
              {title}
            </h3>
          </div>

          {/* Subitem count — bottom-left */}
          <div
            style={{
              position: 'absolute',
              bottom: 14,
              left: 14,
              right: 60,
              fontSize: 12,
              color: '#FFFFFFCC',
              textShadow: '0 1px 3px #00000060',
              zIndex: 2,
            }}
          >
            {countLabel}
          </div>

          {/* CTA play / video icon — bottom-right */}
          <div
            style={{
              position: 'absolute',
              bottom: 12,
              right: 12,
              zIndex: 3,
            }}
          >
            {isQuickTap ? (
              <WebAppQuickTapVideoIcon size={32} />
            ) : (
              <WebAppSessionPlayIcon size={32} />
            )}
          </div>
        </div>
      </div>

      {/* preview-foot — URL + Open link */}
      <div
        className="flex items-center justify-between"
        style={{
          padding: '10px 14px',
          borderTop: '1px solid #E8E0CF',
          background: '#FAF4EA',
          fontSize: 11,
          gap: 8,
        }}
      >
        <span
          className="font-mono text-text-muted"
          style={{
            fontSize: 10.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
            minWidth: 0,
          }}
        >
          {previewPath}
        </span>
        {universalLink && (
          <a
            href={universalLink}
            target="_blank"
            rel="noopener noreferrer"
            className="font-condensed font-bold uppercase no-underline"
            style={{
              fontSize: 10.5,
              letterSpacing: '0.08em',
              color: '#5C7E72',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              whiteSpace: 'nowrap',
            }}
          >
            Open ↗
          </a>
        )}
      </div>
    </RailCard>
  );
}
